import { useState } from "react";
import { base44 } from "@/api/base44Client";
import LaundryOrderBuilder from "@/components/laundry/LaundryOrderBuilder";

const generateOrderNumber = () => {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(2);
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `WS-${yy}${mm}${dd}-${rand}`;
};

const T = {
  ar: {
    title: "طلب استلام الملابس",
    subtitle: "لا تحتاج لتحديد الخدمات الآن — سنتولى الفرز والتقييم عند الاستلام",
    modeQuick: "طلب سريع",
    modeDetailed: "طلب تفصيلي",
    modeQuickHint: "طلب استلام فقط — الفرز والفاتورة عند الاستلام",
    modeDetailedHint: "اختر الأصناف لمعرفة التكلفة التقديرية مع كود الخصم",
    contactSection: "👤 بيانات التواصل",
    nameLabel: "الاسم الكامل *",
    namePlaceholder: "أدخل اسمك الكامل",
    phoneLabel: "رقم الجوال *",
    addressSection: "📍 عنوان الاستلام",
    addressLabel: "العنوان التفصيلي *",
    addressPlaceholder: "الشارع، رقم المبنى...",
    districtLabel: "الحي",
    districtPlaceholder: "اسم الحي",
    locationBtn: "📍 تحديد موقعي على الخريطة",
    locationLoading: "⏳ جاري تحديد الموقع...",
    locationDone: "✅ تم تحديد الموقع",
    locationError: "تعذّر تحديد الموقع، تأكد من منح الإذن",
    locationLabel: "رابط الموقع على الخريطة",
    timeSection: "🕐 وقت الاستلام المفضل",
    dateLabel: "تاريخ الاستلام",
    timeLabel: "الوقت المفضل",
    notesLabel: "ملاحظات إضافية",
    notesPlaceholder: "أي تعليمات خاصة...",
    errorRequired: "يرجى تعبئة الحقول المطلوبة",
    errorPhone: "رقم الجوال غير صحيح (يجب أن يبدأ بـ 05 ويتكون من 10 أرقام)",
    submit: "📦 أرسل طلب الاستلام",
    submitting: "⏳ جاري الإرسال...",
    tip: "💡 بعد استلام ملابسك، سيتم فرزها وتحديد الخدمات والأسعار، ثم إرسال الفاتورة",
    successTitle: "تم استلام طلبك!",
    successMsg: "سيتواصل معك المندوب قريباً لاستلام ملابسك",
    orderLabel: "رقم طلبك",
    orderHint: "احتفظ بهذا الرقم لتتبع طلبك",
    trackBtn: "🔍 تتبع حالة طلبي",
  },
  en: {
    title: "Laundry Pickup Request",
    subtitle: "No need to select services now — we'll sort and evaluate upon pickup",
    modeQuick: "Quick Request",
    modeDetailed: "Detailed Order",
    modeQuickHint: "Pickup only — sorting & invoice upon pickup",
    modeDetailedHint: "Select items to see estimated cost with discount code",
    contactSection: "👤 Contact Details",
    nameLabel: "Full Name *",
    namePlaceholder: "Enter your full name",
    phoneLabel: "Mobile Number *",
    addressSection: "📍 Pickup Address",
    addressLabel: "Detailed Address *",
    addressPlaceholder: "Street, building number...",
    districtLabel: "District",
    districtPlaceholder: "District name",
    locationBtn: "📍 Share My Location",
    locationLoading: "⏳ Getting location...",
    locationDone: "✅ Location set",
    locationError: "Could not get location, please allow permission",
    locationLabel: "Map Location Link",
    timeSection: "🕐 Preferred Pickup Time",
    dateLabel: "Pickup Date",
    timeLabel: "Preferred Time",
    notesLabel: "Additional Notes",
    notesPlaceholder: "Any special instructions...",
    errorRequired: "Please fill in the required fields",
    errorPhone: "Invalid mobile number (must start with 05, 10 digits)",
    submit: "📦 Send Pickup Request",
    submitting: "⏳ Sending...",
    tip: "💡 After we pick up your clothes, we'll sort them, determine services and prices, then send you an invoice for approval",
    successTitle: "Order Received!",
    successMsg: "Our delegate will contact you soon to pick up your clothes",
    orderLabel: "Your Order Number",
    orderHint: "Keep this number to track your order",
    trackBtn: "🔍 Track My Order",
  }
};

export default function LaundryOrderForm({ onSuccess, lang = "ar", services = [] }) {
  const tr = T[lang] || T.ar;
  const [mode, setMode] = useState("quick");
  const [cartData, setCartData] = useState({ services: [], subtotal: 0, discount: 0, total_amount: 0, coupon_code: null });
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_address: "",
    district: "",
    pickup_date: "",
    pickup_time: "",
    notes: "",
    location_url: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [locLoading, setLocLoading] = useState(false);
  const [locError, setLocError] = useState("");

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setLocError(tr.locationError);
      return;
    }
    setLocLoading(true);
    setLocError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const mapsUrl = `https://maps.google.com/?q=${latitude},${longitude}`;
        update("location_url", mapsUrl);
        setLocLoading(false);
      },
      () => {
        setLocError(tr.locationError);
        setLocLoading(false);
      },
      { timeout: 10000 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.customer_name || !form.customer_phone || !form.customer_address) {
      setError(tr.errorRequired);
      return;
    }
    if (!/^05\d{8}$/.test(form.customer_phone)) {
      setError(tr.errorPhone);
      return;
    }
    setLoading(true);
    const pickupTime = [form.pickup_date, form.pickup_time].filter(Boolean).join(" - ");
    const order = await base44.entities.LaundryOrder.create({
      ...form,
      pickup_time: pickupTime || null,
      order_number: generateOrderNumber(),
      status: "pending",
      ...(mode === "detailed" && cartData.services.length > 0
        ? {
            services: cartData.services,
            subtotal: cartData.subtotal,
            discount: cartData.discount,
            total_amount: cartData.total_amount,
            coupon_code: cartData.coupon_code,
          }
        : {}),
    });
    setDone(order);
    setLoading(false);
  };

  if (done) {
    return (
      <div className="text-center space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg border border-green-100 dark:border-green-900">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-2">{tr.successTitle}</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">{tr.successMsg}</p>
          <div className="bg-cyan-50 dark:bg-cyan-900/30 rounded-2xl p-4 mb-4">
            <p className="text-xs text-cyan-600 dark:text-cyan-400 font-medium mb-1">{tr.orderLabel}</p>
            <p className="text-2xl font-black text-cyan-700 dark:text-cyan-300 tracking-wider">{done.order_number}</p>
            <p className="text-xs text-gray-500 mt-1">{tr.orderHint}</p>
          </div>
        </div>
        <button
          onClick={onSuccess}
          className="w-full bg-cyan-600 text-white font-bold py-4 rounded-2xl hover:bg-cyan-700 transition-all"
        >
          {tr.trackBtn}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="text-center">
        <h2 className="text-xl font-black text-gray-900 dark:text-white">{tr.title}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{tr.subtitle}</p>
      </div>

      {/* Mode Toggle */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-2 shadow-sm border border-gray-100 dark:border-gray-700 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setMode("quick")}
          className={`py-2.5 rounded-xl text-sm font-bold transition-all ${
            mode === "quick"
              ? "bg-cyan-600 text-white shadow-md"
              : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
          }`}
        >
          ⚡ {tr.modeQuick}
        </button>
        <button
          type="button"
          onClick={() => setMode("detailed")}
          className={`py-2.5 rounded-xl text-sm font-bold transition-all ${
            mode === "detailed"
              ? "bg-cyan-600 text-white shadow-md"
              : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
          }`}
        >
          🧺 {tr.modeDetailed}
        </button>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 text-center -mt-2">
        {mode === "quick" ? tr.modeQuickHint : tr.modeDetailedHint}
      </p>

      {mode === "detailed" && (
        <LaundryOrderBuilder services={services} lang={lang} onChange={setCartData} />
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 space-y-4">
          <h3 className="font-bold text-gray-700 dark:text-gray-300 text-sm">{tr.contactSection}</h3>
          <div>
            <label className="text-xs font-bold text-gray-600 dark:text-gray-400 block mb-1">{tr.nameLabel}</label>
            <input
              type="text"
              value={form.customer_name}
              onChange={e => update("customer_name", e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all text-gray-900 dark:text-white"
              placeholder={tr.namePlaceholder}
            />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-600 dark:text-gray-400 block mb-1">{tr.phoneLabel}</label>
            <input
              type="tel"
              value={form.customer_phone}
              onChange={e => update("customer_phone", e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all text-gray-900 dark:text-white"
              placeholder="05xxxxxxxx"
              dir="ltr"
            />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 space-y-4">
          <h3 className="font-bold text-gray-700 dark:text-gray-300 text-sm">{tr.addressSection}</h3>
          <div>
            <label className="text-xs font-bold text-gray-600 dark:text-gray-400 block mb-1">{tr.addressLabel}</label>
            <input
              type="text"
              value={form.customer_address}
              onChange={e => update("customer_address", e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all text-gray-900 dark:text-white"
              placeholder={tr.addressPlaceholder}
            />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-600 dark:text-gray-400 block mb-1">{tr.districtLabel}</label>
            <input
              type="text"
              value={form.district}
              onChange={e => update("district", e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all text-gray-900 dark:text-white"
              placeholder={tr.districtPlaceholder}
            />
          </div>

          {/* Location button */}
          <div>
            <button
              type="button"
              onClick={handleGetLocation}
              disabled={locLoading}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all border-2 ${
                form.location_url
                  ? "border-green-400 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
                  : "border-dashed border-cyan-400 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-900/30"
              } disabled:opacity-60`}
            >
              {locLoading ? tr.locationLoading : form.location_url ? tr.locationDone : tr.locationBtn}
            </button>
            {locError && <p className="text-xs text-red-500 mt-1 text-center">{locError}</p>}
            {form.location_url && (
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="url"
                  readOnly
                  value={form.location_url}
                  className="flex-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-xs text-gray-500 dark:text-gray-400 outline-none"
                  dir="ltr"
                />
                <a
                  href={form.location_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 px-3 py-2 rounded-xl font-bold hover:bg-cyan-200 transition-all"
                >
                  🗺️
                </a>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 space-y-4">
          <h3 className="font-bold text-gray-700 dark:text-gray-300 text-sm">{tr.timeSection}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-gray-600 dark:text-gray-400 block mb-1">{tr.dateLabel}</label>
              <input
                type="date"
                value={form.pickup_date}
                min={new Date().toISOString().slice(0, 10)}
                onChange={e => update("pickup_date", e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 dark:text-gray-400 block mb-1">{tr.timeLabel}</label>
              <input
                type="time"
                value={form.pickup_time}
                onChange={e => update("pickup_time", e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
          <label className="text-xs font-bold text-gray-600 dark:text-gray-400 block mb-2">{tr.notesLabel}</label>
          <textarea
            value={form.notes}
            onChange={e => update("notes", e.target.value)}
            rows={3}
            className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all text-gray-900 dark:text-white resize-none"
            placeholder={tr.notesPlaceholder}
          />
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-xl px-4 py-3 text-sm text-center">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-black py-4 rounded-2xl shadow-lg hover:opacity-90 transition-all disabled:opacity-60 text-base"
        >
          {loading ? tr.submitting : tr.submit}
        </button>

        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-4 text-center">
          <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">{tr.tip}</p>
        </div>
      </form>
    </div>
  );
}